import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
  /** Extra content under the message — a reason field, a list of consequences.
   *  Added so a confirmation that must CAPTURE something does not need a second
   *  dialog component beside this one. */
  children?: React.ReactNode;
  /** Hold the confirm button until the dialog's own input is satisfied. */
  confirmDisabled?: boolean;
}

const VARIANT_STYLES = {
  danger: {
    iconBg: 'bg-danger-50',
    iconColor: 'text-status-red',
    button: 'bg-status-red hover:bg-danger-600 focus-visible:ring-danger-500',
  },
  warning: {
    iconBg: 'bg-warning-50',
    iconColor: 'text-status-yellow',
    // 700, not 500, and for the same reason .btn-primary uses brand-700: white
    // on warning-500 is 2.15:1. It was worse before this migration — the
    // status-yellow it replaced was 1.92:1 — and the `.bg-yellow-500` override
    // that used to patch this exact problem does not match the new class name.
    // warning-700 with white is 5.02:1, which passes AA at any size.
    button: 'bg-warning-700 hover:bg-warning-800 focus-visible:ring-warning-500',
  },
  info: {
    iconBg: 'bg-info-50',
    iconColor: 'text-status-blue',
    button: 'bg-status-blue hover:bg-info-600 focus-visible:ring-info-500',
  },
};

export default function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  isLoading = false,
  children,
  confirmDisabled = false,
}: ConfirmDialogProps) {
  const styles = VARIANT_STYLES[variant];

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-scrim/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-surface p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex gap-4">
                  <div
                    className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${styles.iconBg}`}
                  >
                    <ExclamationTriangleIcon className={`h-5 w-5 ${styles.iconColor}`} />
                  </div>
                  <div>
                    <Dialog.Title as="h3" className="text-base font-semibold text-content">
                      {title}
                    </Dialog.Title>
                    <p className="mt-2 whitespace-pre-line text-sm text-content-muted">{message}</p>
                    {children && <div className="mt-3">{children}</div>}
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    className="btn-secondary min-h-[44px]"
                    onClick={onClose}
                    disabled={isLoading}>
                    {cancelLabel}
                  </button>
                  <button
                    type="button"
                    className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 ${styles.button}`}
                    onClick={onConfirm}
                    disabled={isLoading || confirmDisabled}>
                    {isLoading ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                        Processing...
                      </>
                    ) : (
                      confirmLabel
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
